import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { AdminPage } from '@/admin/AdminShell'
import { ApiError } from '@/lib/api'
import {
  askAssistant,
  fetchAssistantHistory,
  itemPath,
  splitLabels,
  type AssistantItem,
  type AssistantReply,
} from './assistantApi'

/**
 * Ассистент по данным CRM (тикет #136): вопрос словами — ответ со ссылками на карточки.
 *
 * - **Что понято — на экране**: инструмент и параметры, которые исполнил сервер. Неверное
 *   прочтение вопроса видно сразу, а не прячется за правдоподобным ответом.
 * - **Отказ — словами, а не пустым списком.** «Нет доступа к сделкам сотрудника» и «таких сделок
 *   нет» — разные ответы и выглядят по-разному: пустота читалась бы как «всё в порядке».
 * - **Список — от сервера.** Записи пришли из выборки с правами спрашивающего; текст модели лишь
 *   ссылается на них метками `[S1]`, а выдуманные метки сервер вырезал и назвал.
 */
export function AssistantPage() {
  const { t } = useApp()
  const [params, setParams] = useSearchParams()
  const [question, setQuestion] = useState('')
  // #94: the conversation lives in the address (`?session=`), so a reload or a link back
  // does not lose it; the server returns only the caller's own messages of that session.
  const [sessionId, setSessionId] = useState<string | null>(() => params.get('session'))
  const [replies, setReplies] = useState<AssistantReply[]>([])
  const [restoredFor, setRestoredFor] = useState<string | null>(null)
  const history = useQuery({
    queryKey: ['assistant-session', sessionId],
    enabled: sessionId !== null && restoredFor !== sessionId && replies.length === 0,
    queryFn: ({ signal }) => fetchAssistantHistory(sessionId!, signal),
    retry: false,
  })
  if (history.data && sessionId !== null && restoredFor !== sessionId) {
    // Adjusting state during render (not in an effect): the restored conversation is shown
    // in the same paint as the answer that brought it.
    setRestoredFor(sessionId)
    setReplies(history.data)
  }
  const ask = useMutation({
    mutationFn: (text: string) => askAssistant(text, sessionId),
    onSuccess: (reply) => {
      setSessionId(reply.sessionId)
      setRestoredFor(reply.sessionId)
      setReplies((list) => [...list, reply])
      setQuestion('')
      if (params.get('session') !== reply.sessionId) {
        setParams((was) => {
          const next = new URLSearchParams(was)
          next.set('session', reply.sessionId)
          return next
        }, { replace: true })
      }
    },
  })

  const submit = () => {
    const text = question.trim()
    if (!text || ask.isPending) return
    ask.mutate(text)
  }

  return (
    <AdminPage
      sectionKey="nav.section.work"
      titleKey="nav.assistant"
      leadKey="assistant.lead"
      primary={replies.length > 0 ? (
        <button type="button" className="btn btn-secondary" onClick={() => {
          setReplies([])
          setSessionId(null)
          setRestoredFor(null)
          ask.reset()
          setParams((was) => {
            const next = new URLSearchParams(was)
            next.delete('session')
            return next
          }, { replace: true })
        }}>
          {t('assistant.newChat')}
        </button>
      ) : undefined}
    >
      <div className="stack" style={{ gap: 12 }}>
        {replies.length === 0 && <p className="muted t-sm">{t('assistant.examples')}</p>}
        {replies.map((reply) => <Exchange key={reply.id} reply={reply} />)}
        {ask.error != null && (
          <div className="ro-banner" role="alert">
            {ask.error instanceof ApiError ? ask.error.message : t('assistant.failed')}
          </div>
        )}
        <form className="stack" style={{ gap: 8 }} onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}>
          <textarea
            className="input"
            rows={3}
            aria-label={t('assistant.question')}
            placeholder={t('assistant.placeholder')}
            value={question}
            maxLength={1000}
            onChange={(event) => setQuestion(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                event.preventDefault()
                submit()
              }
            }}
          />
          <div className="row" style={{ gap: 8 }}>
            <button type="submit" className="btn btn-primary" disabled={ask.isPending || !question.trim()}>
              {ask.isPending ? t('assistant.thinking') : t('assistant.ask')}
            </button>
          </div>
        </form>
      </div>
    </AdminPage>
  )
}

function Exchange({ reply }: { reply: AssistantReply }) {
  const { t, user } = useApp()
  const items = reply.items ?? []
  const byLabel = new Map(items.map((item) => [item.label, item]))
  const refused = reply.kind === 'refusal' || reply.kind === 'unsupported'
  return (
    <article className="card stack" style={{ gap: 8, padding: 12 }} aria-label={t('assistant.exchange')}>
      <div className="strong">{reply.question}</div>
      {reply.intent != null && (
        <div className="muted t-sm" data-testid="assistant-intent">
          {t('assistant.understood')}: {t(`assistant.tool.${reply.intent.tool}`)}
          {describeParams(reply.intent.params)}
        </div>
      )}
      <div
        role={refused ? 'alert' : undefined}
        className={refused ? 'ro-banner' : undefined}
        data-kind={reply.kind}
      >
        {reply.kind === 'refusal' && <span className="strong">{t('assistant.refused')} </span>}
        {reply.kind === 'empty' && <span className="strong">{t('assistant.empty')} </span>}
        {splitLabels(reply.text).map((part, index) => 'label' in part
          ? <LabelLink key={index} item={byLabel.get(part.label)} label={part.label} />
          : <span key={index}>{part.text}</span>)}
      </div>
      {(reply.candidates ?? []).length > 0 && (
        <ul aria-label={t('assistant.candidates')}>
          {(reply.candidates ?? []).map((candidate) => <li key={candidate}>{candidate}</li>)}
        </ul>
      )}
      {items.length > 0 && (
        <ul aria-label={t('assistant.records')} className="stack" style={{ gap: 4 }}>
          {items.map((item) => {
            const path = itemPath(item, user)
            return (
              <li key={item.label}>
                <span className="muted t-sm">[{item.label}] </span>
                {path ? <Link to={path}>{item.title ?? item.id}</Link> : <span>{item.title ?? item.id}</span>}
                {item.subtitle != null && <span className="muted t-sm"> — {item.subtitle}</span>}
              </li>
            )
          })}
        </ul>
      )}
      {reply.table != null && reply.table.rows.length > 0 && (
        <table className="table" aria-label={t('assistant.table')}>
          <thead>
            <tr>{reply.table.columns.map((column) => <th key={column}>{column}</th>)}</tr>
          </thead>
          <tbody>
            {reply.table.rows.map((row, index) => (
              <tr key={index}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell == null ? '' : String(cell)}</td>)}</tr>
            ))}
          </tbody>
        </table>
      )}
      {(reply.droppedLabels ?? []).length > 0 && (
        <div className="muted t-sm" role="note">
          {t('assistant.dropped', { labels: (reply.droppedLabels ?? []).join(', ') })}
        </div>
      )}
      {reply.note != null && <div className="muted t-sm">{reply.note}</div>}
    </article>
  )
}

function LabelLink({ item, label }: { item: AssistantItem | undefined; label: string }) {
  const { user } = useApp()
  const path = item ? itemPath(item, user) : null
  if (!item || !path) return <span>[{label}]</span>
  return <Link to={path} title={item.title ?? undefined}>[{label}]</Link>
}

function describeParams(params: Record<string, unknown> | null | undefined): string {
  if (params == null) return ''
  const parts = Object.entries(params)
    .filter(([, value]) => value != null && typeof value !== 'object')
    .map(([key, value]) => `${key}: ${String(value)}`)
  return parts.length ? ` (${parts.join(', ')})` : ''
}
